import { type FunctionResponse, GoogleGenAI, type Session } from '@google/genai';
import { INPUT_SAMPLE_RATE, LIVE_API_VERSION } from '../../shared/config.ts';
import type { ClientSend, FixtureLine, ServerMessage, ToolResponsePart } from '../../shared/live.ts';
import { sleep } from './clock.ts';

export type TransportHandlers = {
  onMessage(msg: ServerMessage): void;
  onClose(info: { code?: number; reason?: string }): void;
};

/** The live conversation channel. Gemini in real use; Scripted replays a recorded session for deterministic UI tests. */
export interface LiveTransport {
  readonly kind: 'gemini' | 'scripted';
  connect(handlers: TransportHandlers, resumeHandle?: string): Promise<void>;
  sendText(text: string): void;
  sendAudio(b64: string): void;
  sendAudioStreamEnd(): void;
  sendToolResponses(responses: ToolResponsePart[]): void;
  close(): void;
  /** Debug only: drop the socket as a network failure would (reported to onClose as unexpected). */
  simulateDrop?(): void;
}

export class GeminiLiveTransport implements LiveTransport {
  readonly kind = 'gemini' as const;
  private session: Session | null = null;
  // One record per socket. A resume closes the old socket and opens a new one; the old socket's close event can
  // arrive after the new one is up, so "closed by us" and "is current" must be tracked per socket, not per transport.
  private current: { closedByUs: boolean } | null = null;
  private readonly token: string;
  private readonly model: string;

  constructor(token: string, model: string) {
    this.token = token;
    this.model = model;
  }

  async connect(handlers: TransportHandlers, resumeHandle?: string) {
    const conn = { closedByUs: false };
    this.current = conn;
    // The token is the credential; model/prompt/tools are locked in it server-side. Only resumption is ours to set.
    const ai = new GoogleGenAI({ apiKey: this.token, httpOptions: { apiVersion: LIVE_API_VERSION } });
    const session = await ai.live.connect({
      model: this.model,
      config: { sessionResumption: resumeHandle ? { handle: resumeHandle } : {} },
      callbacks: {
        onmessage: (m) => {
          if (this.current === conn) handlers.onMessage(m as ServerMessage);
        },
        onerror: () => {},
        onclose: (e) => {
          if (!conn.closedByUs && this.current === conn) handlers.onClose({ code: e.code, reason: e.reason });
        },
      },
    });
    if (this.current !== conn || conn.closedByUs) {
      session.close(); // closed or superseded while connecting
      return;
    }
    this.session = session;
  }

  sendText(text: string) {
    this.session?.sendRealtimeInput({ text });
  }

  sendAudio(b64: string) {
    this.session?.sendRealtimeInput({ audio: { data: b64, mimeType: `audio/pcm;rate=${INPUT_SAMPLE_RATE}` } });
  }

  sendAudioStreamEnd() {
    this.session?.sendRealtimeInput({ audioStreamEnd: true });
  }

  sendToolResponses(responses: ToolResponsePart[]) {
    // Our scheduling strings match the SDK's FunctionResponseScheduling enum values.
    this.session?.sendToolResponse({ functionResponses: responses as FunctionResponse[] });
  }

  close() {
    if (this.current) this.current.closedByUs = true;
    this.current = null;
    this.session?.close();
    this.session = null;
  }

  simulateDrop() {
    this.session?.close(); // current socket stays "not closed by us", so onClose reports it as unexpected
  }
}

/**
 * Replays a recorded session. Server lines are emitted with their recorded spacing (scaled by `speed`); a client
 * line blocks the replay until the app sends the same kind of message (text typed in the composer, tool responses).
 * Audio sends never block, so replay is independent of the mic.
 */
export class ScriptedTransport implements LiveTransport {
  readonly kind = 'scripted' as const;
  private handlers: TransportHandlers | null = null;
  private waiting: { kind: ClientSend['kind']; resolve: () => void } | null = null;
  private pending: ClientSend['kind'][] = [];
  private stopped = false;
  readonly sent: ClientSend[] = [];
  private readonly lines: FixtureLine[];
  private readonly speed: number;

  constructor(lines: FixtureLine[], speed = 4) {
    this.lines = lines;
    this.speed = speed;
  }

  async connect(handlers: TransportHandlers) {
    this.handlers = handlers;
    void this.run();
  }

  private async run() {
    let lastMs = 0;
    for (const line of this.lines) {
      if (this.stopped) return;
      if (line.dir === 'client') {
        if (line.send.kind === 'audio' || line.send.kind === 'audioStreamEnd') continue;
        await this.waitFor(line.send.kind);
        lastMs = line.ms;
        continue;
      }
      // The worker clock keeps a replay moving while the tab is hidden (see clock.ts).
      await sleep(Math.min(1500, Math.max(0, line.ms - lastMs) / this.speed));
      lastMs = line.ms;
      if (!this.stopped) this.handlers?.onMessage(line.msg);
    }
  }

  private waitFor(kind: ClientSend['kind']) {
    const idx = this.pending.indexOf(kind);
    if (idx !== -1) {
      this.pending.splice(idx, 1);
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.waiting = { kind, resolve };
    });
  }

  private record(send: ClientSend) {
    this.sent.push(send);
    if (this.waiting?.kind === send.kind) {
      const w = this.waiting;
      this.waiting = null;
      w.resolve();
    } else if (send.kind === 'text' || send.kind === 'toolResponse') {
      this.pending.push(send.kind);
    }
  }

  sendText(text: string) {
    this.record({ kind: 'text', text });
  }
  sendAudio(b64: string) {
    this.record({ kind: 'audio', bytes: b64.length });
  }
  sendAudioStreamEnd() {
    this.record({ kind: 'audioStreamEnd' });
  }
  sendToolResponses(responses: ToolResponsePart[]) {
    this.record({ kind: 'toolResponse', responses });
  }
  close() {
    this.stopped = true;
  }
}

export function parseFixture(jsonl: string): FixtureLine[] {
  return jsonl
    .split('\n')
    .filter((l) => l.trim())
    .map((l) => JSON.parse(l) as FixtureLine);
}
